import { useCallback, useContext } from 'react';
import { AnalyticsContext, IAnalyticsContextValue } from 'utils';

export type SelectOpType =
  | 'accessibilityToggle'
  | 'actionTileSelect'
  | 'autoCollapse'
  | 'autoDismiss'
  | 'autoPayToggle'
  | 'autoplayToggle'
  | 'blockedNetworksUpdated'
  | 'buttonClick'
  | 'closedCaptionLanguage'
  | 'closedCaptionStyle'
  | 'closedCaptionTextSize'
  | 'closedCaptionToggle'
  | 'collapseClick'
  | 'curatedCatalogCastAndCrew'
  | 'curatedSearchCastAndCrew'
  | 'curatedSearchEvents'
  | 'curatedSearchNetworks'
  | 'curatedSearchSports'
  | 'curatedSearchTeam'
  | 'dataNoticeToggle'
  | 'dataReset'
  | 'doubleTap'
  | 'dropDown'
  | 'expandClick'
  | 'favoritesUpdated'
  | 'favoriteToggle'
  | 'filterApplied'
  | 'guideNarrationToggle'
  | 'infoBarActivation'
  | 'inputFieldUpdate'
  | 'letterPicker'
  | 'modalLaunch'
  | 'navigationClick'
  | 'networkSelection'
  | 'paginationClick'
  | 'paperlessToggle'
  | 'parentalControlNetworkToggle'
  | 'parentalControlToggle'
  | 'picker'
  | 'pinch'
  | 'playButtonClicked'
  | 'radioSelect'
  | 'ratingRestrictionsUpdated'
  | 'relatedArticleSelection'
  | 'remoteControlBack'
  | 'remoteControlClick'
  | 'sapToggle'
  | 'searchStarted'
  | 'secondaryAudioToggle'
  | 'singleTap'
  | 'sortApplied'
  | 'swipe'
  | 'toggleFlip'
  | 'toolTipLaunch'
  | 'unpinch'
  | 'volumeChange'
  | 'watchListAdd'
  | 'watchListRemove'
  | 'wideScreenToggle';

interface ISelectActionOptions {
  currPageElemStdName?: string;
  msgCategory?: 'navigation' | 'search' | 'contentDiscovery' | 'authentication';
  msgTriggeredBy?: 'user' | 'application';
  opType?: SelectOpType;
  [key: string]: any;
}

interface IPageViewOptions {
  currPageName?: string;
  currPageAppSection?: string;
  currPageIsLazyLoad?: boolean;
  msgTriggeredBy?: 'user' | 'application';
  [key: string]: any;
}

type LoginOpType =
  | 'manualAuth'
  | 'autoAccess'
  | 'resumeAuth'
  | 'verifierAuth'
  | 'tokenExchange'
  | 'ssoAuth'
  | 'appAuthorize'
  | 'trustedAuth'
  | 'biometricAuth'
  | 'tvEverywhere';

interface ILoginStopOptions {
  accountNumber?: string;
  accountGUID?: string;
  [key: string]: any;
}

export const useAnalytics = () => {
  const { analytics } = useContext(AnalyticsContext) as IAnalyticsContextValue;

  const trackSelectAction = useCallback(
    (currPageElemStdName: string, options: ISelectActionOptions = {}) => {
      analytics?.track('selectAction', {
        currPageElemStdName,
        currPageElemUiName: currPageElemStdName,
        msgCategory: 'navigation',
        msgTriggeredBy: 'user',
        opType: 'dropDown',
        ...options,
      });
    },
    [analytics]
  );

  const trackPageView = useCallback(
    (pageName: string, options: IPageViewOptions = {}) => {
      analytics?.trackPageView(pageName, {
        msgTriggeredBy: 'user',
        currPageIsLazyLoad: false,
        ...options,
      });
    },
    [analytics]
  );

  const trackLoginStart = useCallback(
    (opType: LoginOpType) => {
      analytics?.track('loginStart', {
        opType,
      });
    },
    [analytics]
  );

  const trackLoginStop = useCallback(
    (opSuccess: boolean, options: ILoginStopOptions = {}) => {
      analytics?.track('loginStop', {
        opSuccess,
        ...options,
      });
    },
    [analytics]
  );

  const trackApiCall = useCallback(
    (params: {
      /** Request endpoint */
      url: string;
      /** Request method ('get', 'put', etc) */
      method: string;
      /** Determines if response was successful */
      isSuccess: boolean;
      /** Status code from response */
      status?: number;
      /** Time elapsed between req -> res */
      time?: number;
      /** Stringified query params */
      queryParams?: string;
      /** Response message */
      message?: string;
      /** If req sent to BE using quantum/Qube, provide the associated traceID from the response */
      traceId?: string;
    }) => {
      const {
        url,
        isSuccess,
        method,
        status,
        time = 0,
        queryParams,
        message,
        traceId,
      } = params;
      const { hostname, pathname } = new URL(url);
      analytics?.track('apiCall', {
        opSuccess: isSuccess,
        appApiResponseCode: status,
        appApiResponseTimeMs: time,
        appApiHost: hostname,
        appApiPath: pathname,
        appApiHttpVerb: method.toUpperCase(),
        appApiQueryParameters: queryParams,
        appApiResponseText: message,
        appApiTraceId: traceId || 'No traceId',
      });
    },
    [analytics]
  );

  return {
    analytics,
    trackSelectAction,
    trackPageView,
    trackLoginStart,
    trackLoginStop,
    trackApiCall,
  };
};
